// src/client.ts
var FeedbackClient = class {
  constructor({ fetcher, baseUrl = "/api/feedback" }) {
    this.fetcher = fetcher;
    this.baseUrl = baseUrl.replace(/\/$/, "");
  }
  async getPrompt() {
    return this.fetcher(`${this.baseUrl}/prompt/`, { method: "GET" });
  }
  async submit(payload) {
    var _a;
    try {
      return await this.fetcher(`${this.baseUrl}/responses/`, { method: "POST", body: payload });
    } catch (error) {
      if ((error == null ? void 0 : error.status) === 409 || ((_a = error == null ? void 0 : error.response) == null ? void 0 : _a.status) === 409) {
        return { alreadySettled: true };
      }
      throw error;
    }
  }
};

// src/useFeedbackPrompt.ts
import { useCallback, useEffect, useRef, useState } from "react";
var EMPTY = { due: false };
function useFeedbackPrompt({
  client,
  locationKey = "",
  pollMs = 6e4,
  channelName = "huginn",
  enabled = true
}) {
  const [prompt, setPrompt] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const channelRef = useRef(null);
  const refresh = useCallback(async () => {
    if (!enabled) {
      setPrompt(EMPTY);
      setLoading(false);
      return;
    }
    try {
      const body = await client.getPrompt();
      setPrompt(body && typeof body === "object" ? body : EMPTY);
    } catch (e) {
      setPrompt(EMPTY);
    } finally {
      setLoading(false);
    }
  }, [client, enabled]);
  useEffect(() => {
    void refresh();
  }, [refresh, locationKey]);
  useEffect(() => {
    const onFocus = () => void refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);
  useEffect(() => {
    if (!prompt.due || !pollMs) return void 0;
    const timer = setInterval(() => void refresh(), pollMs);
    return () => clearInterval(timer);
  }, [prompt.due, pollMs, refresh]);
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return void 0;
    const channel = new BroadcastChannel(channelName);
    channelRef.current = channel;
    channel.onmessage = (event) => {
      var _a;
      if (((_a = event.data) == null ? void 0 : _a.type) === "settled") void refresh();
    };
    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [channelName, refresh]);
  const settle = useCallback(
    async (payload) => {
      var _a;
      const result = await client.submit(payload);
      (_a = channelRef.current) == null ? void 0 : _a.postMessage({ type: "settled", cycleId: payload.cycle_id });
      setPrompt(EMPTY);
      return result;
    },
    [client]
  );
  const submit = useCallback(
    (answers, context) => settle({ kind: "periodic", cycle_id: prompt.cycle_id, answers, context }),
    [settle, prompt.cycle_id]
  );
  const skip = useCallback(
    () => settle({ kind: "skip", cycle_id: prompt.cycle_id, answers: {} }),
    [settle, prompt.cycle_id]
  );
  const submitAdhoc = useCallback(
    (answers, context) => client.submit({ kind: "adhoc", answers, context }),
    [client]
  );
  return {
    due: Boolean(prompt.due),
    mode: prompt.mode,
    cycleId: prompt.cycle_id,
    periodic: prompt.periodic,
    adhoc: prompt.adhoc,
    loading,
    refresh,
    submit,
    skip,
    submitAdhoc
  };
}

export {
  FeedbackClient,
  useFeedbackPrompt
};
